import React from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import PropertyPage from './pages/PropertyPage';
import LeaveReviewPage from './pages/LeaveReviewPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiesPolicy from './pages/CookiesPolicy';
import TermsConditions from './pages/TermsConditions';
import TestPage from './pages/TestPage';
import HomePage from './pages/HomePage';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import ReviewModal from './components/ReviewModal';
import { Review, ReviewFormData, UserDetails } from './types/review';
import { useReviews } from './hooks/useReviews';
import { trackReviewModalOpen } from './utils/analytics';

// Initial state constants for consistent resets
const INITIAL_USER_DETAILS: UserDetails = {
  name: '',
  email: '',
  phone: undefined,
  isAnonymous: false
};

const INITIAL_REVIEW_DATA: ReviewFormData = {
  property_review: '',
  agent_review: '',
  location_review: '',
  landlord_review: '',
  timeInPropertyYears: 0,
  timeInPropertyMonths: 0,
  dateInProperty: '',
  landlordName: '',
  landlordEmail: '',
  landlordPhone: '',
  propertyRating: 0,
  agentRating: 0,
  landlordRating: 0,
  locationRating: 0,
  images: [],
  propertyOwnership: null,
  hasLettingAgent: null,
  experiencedIssues: [],
  consentCompensationContact: null
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(true);
  const [hasLandlordInfo, setHasLandlordInfo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { reviews, addReview } = useReviews();
  const [userDetails, setUserDetails] = useState<UserDetails>(INITIAL_USER_DETAILS);
  const [newReview, setNewReview] = useState<ReviewFormData>(INITIAL_REVIEW_DATA);

  // Function to reset all review form state
  const resetReviewFormState = () => {
    setNewReview(INITIAL_REVIEW_DATA);
    setUserDetails(INITIAL_USER_DETAILS);
    setShowReviewForm(true); // Always start at Step 1
    setHasLandlordInfo(false);
    setIsSubmitting(false);
  };

  // Function to open review modal with clean state
  const handleOpenReviewModal = () => {
    resetReviewFormState();
    setShowReviewModal(true);
    
    // Track review modal open - determine source from current location
    const currentPath = location.pathname;
    let source: 'homepage' | 'property_page' | 'leave_review_page' = 'homepage';
    if (currentPath.startsWith('/property/')) {
      source = 'property_page';
    } else if (currentPath === '/leave-review') {
      source = 'leave_review_page';
    }
    trackReviewModalOpen(source);
  };

  // Function to cancel review and reset state
  const handleCancelReview = () => {
    resetReviewFormState();
    setShowReviewModal(false);
  };

  // Close review modal when navigating away from property pages
  useEffect(() => {
    // If modal is open and we're not on a property page, close it
    if (showReviewModal && !location.pathname.startsWith('/property/')) {
      handleCancelReview();
    }
  }, [location.pathname, showReviewModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to get address and UDPRN from current URL
  const getAddressAndUdprnFromUrl = () => {
    const pathParts = location.pathname.split('/');
    if (pathParts[1] === 'property' && pathParts[2]) {
      const address = decodeURIComponent(pathParts[2]);
      const searchParams = new URLSearchParams(location.search);
      const udprn = searchParams.get('udprn');
      return { address, udprn };
    }
    return { address: '', udprn: null };
  };
  const handleUserDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowReviewForm(true);
  };

  const handleReviewSubmitted = (review: Review) => {
    addReview(review);
    handleCancelReview();
    
    // Auto reload the page to show the new review
    setTimeout(() => {
      window.location.reload();
    }, 500); // Small delay to ensure modal closes smoothly
  };

  return (
    <>
      <Routes>
        <Route path="/property/:address" element={
          <PropertyPage 
            onReviewClick={handleOpenReviewModal}
          />
        } />
        <Route path="/leave-review" element={<LeaveReviewPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/cookies-policy" element={<CookiesPolicy />} />
        <Route path="/terms-conditions" element={<TermsConditions />} />
        <Route path="/test" element={<TestPage />} />
        <Route path="/" element={
          <HomePage
            showReviewModal={showReviewModal}
            setShowReviewModal={handleOpenReviewModal}
            onAddressSelect={(addr, udprnValue) => {
              navigate(`/property/${encodeURIComponent(addr)}?udprn=${udprnValue}`);
            }}
          />
        } />
      </Routes>
      <Footer showFAQ={location.pathname === '/'} />
      <CookieBanner />

      <ReviewModal
        showReviewModal={showReviewModal}
        setShowReviewModal={setShowReviewModal}
        showReviewForm={showReviewForm}
        setShowReviewForm={setShowReviewForm}
        hasLandlordInfo={hasLandlordInfo}
        setHasLandlordInfo={setHasLandlordInfo}
        userDetails={userDetails}
        setUserDetails={setUserDetails}
        newReview={newReview}
        setNewReview={setNewReview}
        getAddressAndUdprn={getAddressAndUdprnFromUrl}
        onReviewSubmitted={handleReviewSubmitted}
        isSubmitting={isSubmitting}
        onCancel={handleCancelReview}
      />
    </>
  );
}

export default App;